import { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Button, Typography, Checkbox, Select as AntSelect } from "antd";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { SettingOutlined } from "@ant-design/icons";

import BackButton from "components/BackButton";
import {
  TextInput,
  ImageUpload,
  TextEditor,
  Select,
  AutoComplete,
  MultiImageUpload,
} from "components/common";
import ChangeWorkingTimeModal from "components/ChangeWorkingTimeModal";
import {
  setChangeWorkingTimeModal,
} from "providers/GeneralProvider/slice";
import {
  createHealthPlaceRequest,
  updateHealthPlaceRequest,
  getHealthPlaceDetailRequest,
} from "providers/HealthPlaceProvider/slice";
import { getHealthTypeListRequest } from "providers/HealthTypeProvider/slice";
import { WORKING_TIME_TEXT } from "utils/constants";
import Helper from "utils/helpers";
import "./styles.less";

// Validation schema
const createValidationSchema = yup.object().shape({
  placeName: yup.string().required("Vui lòng nhập tên cơ sở y tế"),
  healthType: yup.array().min(1, "Vui lòng chọn ít nhất 1 loại y tế").required("Vui lòng chọn loại y tế"),
  address: yup.string().required("Vui lòng nhập địa chỉ"),
  thumbnail: yup.mixed().required("Vui lòng chọn ảnh thumbnail"),
});

const editValidationSchema = yup.object().shape({
  placeName: yup.string().required("Vui lòng nhập tên cơ sở y tế"),
  healthType: yup.array().min(1, "Vui lòng chọn ít nhất 1 loại y tế").required("Vui lòng chọn loại y tế"),
  address: yup.string().required("Vui lòng nhập địa chỉ"),
});

const HealthCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const { id } = useParams();

  const { isCreating, detail: healthDetail, isLoading } = useSelector((state) => state.healthPlace);
  const { list: healthTypes, isLoading: isLoadingTypes } = useSelector(
    (state) => state.healthType
  );
  const workingTimeModal = useSelector(
    (state) => state.general.workingTimeModal
  );

  const isEditMode = !!id;

  // Load health types and detail (if edit mode) on mount
  useEffect(() => {
    dispatch(getHealthTypeListRequest({ limit: 100 }));

    if (isEditMode) {
      dispatch(getHealthPlaceDetailRequest({ id }));
    }
  }, [dispatch, id, isEditMode]);

  // Format health types for select options
  const healthTypeOptions = (healthTypes || [])
    .filter(type => !type.isDeleted)
    .map(type => ({
      value: type._id,
      label: type.title,
    }));

  // Initialize working time for 7 days based on method
  const initWorkingTime = (method = "always_open") => {
    if (method === "always_open") {
      return Array(7).fill(null).map(() => ({
        time: [],
        alwaysClose: false,
        alwaysOpen: true,
      }));
    } else if (method === "always_close") {
      return Array(7).fill(null).map(() => ({
        time: [],
        alwaysClose: true,
        alwaysOpen: false,
      }));
    } else {
      return Array(7).fill(null).map(() => ({
        time: [],
        alwaysClose: false,
        alwaysOpen: false,
      }));
    }
  };

  const initialValues = isEditMode && healthDetail._id ? {
    placeName: healthDetail.placeName || "",
    healthType: Array.isArray(healthDetail.healthType)
      ? healthDetail.healthType.map(type => type._id || type)
      : [],
    address: healthDetail.address || "",
    phone: healthDetail.phone || "",
    emergencyService: healthDetail.emergencyService || false,
    workingTimeMethod: healthDetail.workingTimeMethod || (healthDetail.workingHour?.length > 0 ? "custom" : "always_open"),
    workingTime: healthDetail.workingTimeMethod === "custom" && healthDetail.workingHour?.length > 0
      ? healthDetail.workingHour
      : initWorkingTime(healthDetail.workingTimeMethod || "always_open"),
    description: healthDetail.description || "",
    thumbnail: healthDetail.thumbnail || null,
    images: healthDetail.images || [],
    lat: healthDetail.location?.coordinates?.[1] || null,
    lng: healthDetail.location?.coordinates?.[0] || null,
  } : {
    placeName: "",
    healthType: [],
    address: "",
    phone: "",
    emergencyService: false,
    workingTimeMethod: "always_open",
    workingTime: initWorkingTime(),
    description: "",
    thumbnail: null,
    images: [],
    lat: null,
    lng: null,
  };

  const handleSubmit = async (values) => {
    try {
      // Get lat/lng from address
      const latLng = await Helper.getLatLng(values.address, {
        lng: values.lng,
        lat: values.lat,
      });

      // Prepare data - Only 11 fields as per API spec
      const data = {
        placeName: values.placeName,
        healthType: values.healthType,
        address: values.address,
        phone: values.phone || undefined,
        emergencyService: values.emergencyService || false,
        latitude: latLng.lat,
        longitude: latLng.lng,
        workingTimeMethod: values.workingTimeMethod,
        workingTime: values.workingTime,
        description: values.description || undefined,
        thumbnail: values.thumbnail?.url || values.thumbnail,
        images: values.images?.map(img => img.url || img) || [],
      };

      if (isEditMode) {
        // Update existing place
        dispatch(updateHealthPlaceRequest({ id, data }))
          .then(() => {
            navigate(`/health/${id}`);
          });
      } else {
        // Create new place
        dispatch(createHealthPlaceRequest({ data }))
          .then(() => {
            navigate("/health", { replace: true });
          });
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating health place:" : "Error creating health place:", error);
    }
  };

  const handleChangeWorkingTimeModal = (values) => {
    dispatch(
      setChangeWorkingTimeModal({
        isOpen: true,
        data: {
          workingTimeMethod: values.workingTimeMethod,
          workingTime: values.workingTime,
        },
      })
    );
  };

  const handleChangeWorkingTime = (values) => {
    formikRef.current?.setFieldValue(
      "workingTimeMethod",
      values.workingTimeMethod
    );
    formikRef.current?.setFieldValue("workingTime", values.workingTime);
  };

  const handleBack = () => {
    navigate("/health");
  };

  return (
    <div className="health-create-wrapper">
      <Row className="health-create-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>
          {isEditMode ? "Chỉnh sửa cơ sở y tế" : "Thêm cơ sở y tế mới"}
        </Typography.Title>
      </Row>

      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEditMode ? editValidationSchema : createValidationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ handleSubmit, values, isValid, setFieldValue }) => (
          <Form>
            {workingTimeModal.isOpen && (
              <ChangeWorkingTimeModal
                workingTimeModal={workingTimeModal}
                handleChangeWorkingTime={handleChangeWorkingTime}
              />
            )}

            <div className="content-wrapper">
              <Row gutter={24}>
                {/* Left Column - Basic Info */}
                <Col span={14}>
                  <div className="form-section">
                    <div className="section-title">Thông tin cơ bản</div>

                    <Row gutter={16} align="top">
                      <Col span={5}>
                        <div className="thumbnail-upload">
                          <ImageUpload name="thumbnail" disableCrop={true} />
                        </div>
                      </Col>

                      <Col span={19}>
                        <Row gutter={[16, 20]}>
                          <Col span={12}>
                            <div className="field-label">Tên cơ sở</div>
                            <TextInput
                              name="placeName"
                              placeholder="Nhập tên cơ sở"
                              size="small"
                              required
                            />
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Loại y tế (có thể chọn nhiều)</div>
                            <AntSelect
                              mode="multiple"
                              style={{ width: '100%' }}
                              placeholder="Chọn 1 hoặc nhiều loại y tế"
                              options={healthTypeOptions}
                              value={values.healthType}
                              onChange={(value) => setFieldValue("healthType", value)}
                              loading={isLoadingTypes}
                              size="small"
                              showSearch
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                            />
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Số điện thoại</div>
                            <TextInput
                              name="phone"
                              placeholder="Nhập số điện thoại"
                              size="small"
                            />
                          </Col>

                          <Col span={24}>
                            <div className="field-label">Địa chỉ</div>
                            <AutoComplete
                              name="address"
                              placeholder="Nhập địa chỉ đầy đủ"
                              size="small"
                              required
                            />
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Giờ làm việc</div>
                            <Row gutter={8} align="middle">
                              <Col flex="auto">
                                <TextInput
                                  name="workingTimeMethod"
                                  value={WORKING_TIME_TEXT[values.workingTimeMethod]}
                                  disabled
                                  size="small"
                                />
                              </Col>
                              <Col>
                                <Button
                                  icon={<SettingOutlined />}
                                  onClick={() => handleChangeWorkingTimeModal(values)}
                                  size="small"
                                />
                              </Col>
                            </Row>
                          </Col>

                          <Col span={12}>
                            <div className="field-label">
                              <Checkbox
                                checked={values.emergencyService}
                                onChange={(e) => setFieldValue("emergencyService", e.target.checked)}
                              >
                                Cấp cứu 24/7
                              </Checkbox>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>

                  <div className="form-section">
                    <div className="section-title">Mô tả chi tiết</div>

                    <Row gutter={[16, 20]}>
                      <Col span={24}>
                        <div className="field-label">Mô tả</div>
                        <TextEditor
                          name="description"
                          placeholder="Nhập mô tả chi tiết về cơ sở..."
                          rows={4}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* Right Column - Images */}
                <Col span={10}>
                  <div className="form-section">
                    <div className="section-title">Hình ảnh</div>
                    <div className="field-label">Hình ảnh cơ sở</div>
                    <MultiImageUpload name="images" maxCount={10} />
                  </div>
                </Col>
              </Row>

              <Row justify="center" className="form-actions">
                <Col>
                  <Button size="large" onClick={handleBack}>
                    Hủy
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    loading={isCreating}
                    disabled={!isValid}
                    onClick={handleSubmit}
                  >
                    {isEditMode ? "Cập nhật" : "Tạo cơ sở"}
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default HealthCreate;
